// @ts-nocheck

import { Button, Icon } from '@lmc/ui-kit'
import type React from 'react'
import { PureComponent, type ReactNode } from 'react'
import AnimateHeight from 'react-animate-height'
import PropertyFilterList from './PropertyFilterList'

type Filter = {
  value: string
  badgeColor?: string
  badgeText?: string
  icon?: string
  iconColor?: string
  picture?: string
  label?: string
}

type PropertyFiltersProps = {
  activeFilters: { [key: string]: string[] }
  allFilters: { [key: string]: { filters: Filter[] } }
  expandedView: boolean
  onChange: (filter: { key: string; value: string }) => void
  style?: React.CSSProperties
  testId?: string
}

type PropertyFiltersState = {
  hasOpenList: boolean
  openList: string | null
}

export default class PropertyFilters extends PureComponent<
  PropertyFiltersProps,
  PropertyFiltersState
> {
  private list: HTMLDivElement | null = null

  constructor(props: PropertyFiltersProps) {
    super(props)
    this.state = {
      hasOpenList: false,
      openList: null
    }
  }

  componentDidMount() {
    window.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount() {
    window.removeEventListener('mousedown', this.handleClickOutside)
  }

  private setListRef = (node: HTMLDivElement | null) => {
    this.list = node
  }

  private handleClickOutside = (event: MouseEvent) => {
    if (this.list && !this.list.contains(event.target as Node)) {
      this.setState({ hasOpenList: false })
    }
  }

  private toggleList = (key: string) => {
    if (this.state.openList === key) {
      this.setState((prevState) => ({
        hasOpenList: !prevState.hasOpenList
      }))
    } else {
      this.setState({
        hasOpenList: true,
        openList: key
      })
    }
  }

  private renderButton = (key: string, index: number) => {
    const { allFilters, testId } = this.props
    const { hasOpenList, openList } = this.state

    const filter = allFilters[key]
    const isOpen = hasOpenList && openList === key
    const buttonTestId = testId ? `${testId}-filter-btn-${index}` : undefined

    return (
      <Button
        className="lmc-filter-list-button"
        key={index}
        variant="default"
        onClick={() => this.toggleList(key)}
        iconPositionRight
        style={{ margin: 2 }}
        icon={<Icon name={isOpen ? 'UpOutlined' : 'DownOutlined'} />}
        data-testid={buttonTestId}
      >
        {filter.label}
      </Button>
    )
  }

  private renderRegularLists = () => {
    const { activeFilters, allFilters } = this.props
    const { openList } = this.state

    const filters = allFilters[openList] && allFilters[openList].filters
    const active = activeFilters && activeFilters[openList]

    return this.renderList({ active, filters, filterKey: openList })
  }

  private renderList = (filterData: {
    active: string[] | undefined
    filters: Filter[] | undefined
    filterKey: string
  }) => {
    const { hasOpenList, openList } = this.state
    const isSuInvolved = filterData.filterKey === 'doneBySu'

    return (
      <AnimateHeight
        animateOpacity
        duration={200}
        height={!!hasOpenList && openList === filterData.filterKey ? 'auto' : 0}
      >
        <PropertyFilterList
          active={filterData.active}
          filters={filterData.filters}
          filterKey={filterData.filterKey}
          onChange={this.props.onChange}
          showSearch={!isSuInvolved}
          style={styles.filterList}
          showClear={!isSuInvolved}
          singleSelect={isSuInvolved}
          testId={
            this.props.testId
              ? `${this.props.testId}-${filterData.filterKey}`
              : undefined
          }
        />
      </AnimateHeight>
    )
  }

  private renderRegularView = () => {
    const { allFilters } = this.props
    const filterKeys = Object.keys(allFilters)
    return (
      <div style={styles.wrapper}>
        <div style={styles.buttonsContainer}>
          {filterKeys.map(this.renderButton)}
        </div>
        {this.renderRegularLists()}
      </div>
    )
  }

  private renderExpandedView = () => {
    const { activeFilters, allFilters } = this.props
    const filterKeys = Object.keys(allFilters)
    return (
      <div style={styles.wrapper}>
        <div style={styles.buttonsContainer}>
          {filterKeys.map((key, index) => {
            const active = activeFilters && activeFilters[key]
            const filters = allFilters[key] && allFilters[key].filters
            return (
              <div key={key}>
                <div style={styles.expandedViewContainer}>
                  {this.renderButton(key, index)}
                </div>
                <div style={styles.expandedViewListContainer}>
                  {this.renderList({
                    active,
                    filters,
                    filterKey: key
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  render(): ReactNode {
    const { allFilters, expandedView, style, testId } = this.props
    if (!allFilters) return null
    return (
      <div
        style={{
          ...styles.wrapper,
          ...style
        }}
        ref={this.setListRef}
        data-testid={testId ? `${testId}-filters` : undefined}
      >
        {expandedView ? this.renderExpandedView() : this.renderRegularView()}
      </div>
    )
  }
}

const styles = {
  buttonsContainer: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row wrap'
  },
  expandedViewListContainer: {
    width: 300,
    position: 'absolute',
    zIndex: 1
  },
  expandedViewContainer: {
    position: 'relative'
  },
  wrapper: {
    maxWidth: '100%'
  }
}
